// Recipient PWA home. Mobile-first for HT family members: send a request, and
// — the key action — confirm delivery with the PIN once a merchant marks an
// order delivered. Wired to real orders (requests.list for the recipient role).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, ShoppingBag, History, CheckCircle2, Truck, Loader2,
  LayoutDashboard, ShieldCheck, Settings, KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { PortalShell, type NavSection } from '../components/shared/PortalShell';
import { Card, SectionHeader, StatusPill, EmptyState } from '../components/shared/primitives';
import { Button } from '../components/stripe-ui/Button';
import { requests, type ServiceRequestWithItems } from '../lib/api';

const RECIPIENT_NAV: NavSection[] = [
  { items: [
    { id: 'home',    label: 'Home',    icon: LayoutDashboard, href: '/recipient' },
    { id: 'history', label: 'History', icon: History,         href: '/recipient/history' },
  ]},
  { title: 'Account', items: [
    { id: 'settings', label: 'Settings', icon: Settings, href: '/recipient/settings' },
  ]},
];

const TERMINAL = ['confirmed', 'cancelled', 'refunded'];

interface RecipientProfile { full_name?: string; city?: { name: string } }

export function RecipientHome() {
  const { user } = useAuth();
  const rp = (user?.recipient_profile ?? {}) as RecipientProfile;
  const firstName = (rp.full_name ?? 'there').split(' ')[0];

  const q = useQuery({ queryKey: ['recipient', 'requests'], queryFn: () => requests.list() });
  const all = q.data ?? [];
  const active = all.filter((r) => !TERMINAL.includes(r.status));
  const toConfirm = active.filter((r) => r.status === 'delivered');
  const others = active.filter((r) => r.status !== 'delivered');
  const recent = all.filter((r) => r.status === 'confirmed').slice(0, 3);

  return (
    <PortalShell
      nav={RECIPIENT_NAV}
      activeId="home"
      title={`Bonjou, ${firstName}`}
      subtitle="Your family in the diaspora can buy what you need."
    >
      <div className="space-y-5 max-w-2xl mx-auto">
        {/* Awaiting confirmation — the most important action, surfaced first. */}
        {toConfirm.length > 0 && (
          <Card className="border-emerald-200 bg-emerald-50/40">
            <SectionHeader title="Confirm your delivery" subtitle="A merchant marked these delivered — enter the code you received to confirm." />
            <div className="space-y-3 mt-1">
              {toConfirm.map((r) => <ConfirmRow key={r.id} r={r} />)}
            </div>
          </Card>
        )}

        {/* Hero CTA */}
        <Card className="bg-gradient-to-br from-brand-50 to-amber-50/40 border-brand-100">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-700">Need something?</p>
          <h2 className="mt-1 text-[22px] font-semibold tracking-tight text-slate-900">Send a request — your family will see it.</h2>
          <p className="mt-1 text-[13px] text-slate-600">Groceries, pharmacy, school tuition, or mobile top-up. A merchant near you delivers, and you confirm with a code.</p>
          <Link to="/recipient/request/new" className="block mt-4">
            <Button tone="primary" size="lg" className="w-full"><Plus className="w-4 h-4 mr-1.5" /> New request</Button>
          </Link>
        </Card>

        {/* Active (not yet delivered) */}
        <Card>
          <SectionHeader title="Active requests" subtitle="Awaiting a quote, payment, or merchant delivery." />
          {q.isLoading ? (
            <div className="text-center py-8 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
          ) : others.length === 0 && toConfirm.length === 0 ? (
            <EmptyState icon={ShoppingBag} title="No active requests" body="Send a new request above and your family will be notified." />
          ) : (
            <div className="divide-y divide-slate-100 -mx-1 px-1">
              {others.map((r) => (
                <div key={r.id} className="py-3 flex items-start gap-3">
                  <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg shrink-0 bg-brand-50 text-brand-600"><Truck className="w-4 h-4" /></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[12px] text-cyan-700">REQ-{String(r.id).padStart(4, '0')}</span>
                      <span className="text-[13px] font-semibold text-slate-900">· {r.category?.label ?? '—'}</span>
                      <StatusPill status={r.status} />
                    </div>
                    <p className="mt-0.5 text-[12px] text-slate-500">{r.cart_item_count} item{r.cart_item_count === 1 ? '' : 's'}{r.city?.name ? ` · ${r.city.name}` : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Recent confirmed */}
        {recent.length > 0 && (
          <Card>
            <SectionHeader title="Recent deliveries" actions={<Link to="/recipient/history" className="text-[12px] font-semibold text-brand-600 hover:underline">History →</Link>} />
            <div className="divide-y divide-slate-100 -mx-1 px-1">
              {recent.map((r) => (
                <div key={r.id} className="py-3 flex items-center gap-3">
                  <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg shrink-0 bg-emerald-50 text-emerald-600"><CheckCircle2 className="w-4 h-4" /></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-slate-900">REQ-{String(r.id).padStart(4, '0')} · {r.category?.label ?? '—'}</p>
                    <p className="text-[11.5px] text-slate-500">Delivered &amp; confirmed</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <p className="text-[11px] text-slate-400 inline-flex items-center gap-1 justify-center">
          <ShieldCheck className="w-3 h-3" /> Add VoyeLakay to your home screen for one-tap access.
        </p>
      </div>
    </PortalShell>
  );
}

function ConfirmRow({ r }: { r: ServiceRequestWithItems }) {
  const qc = useQueryClient();
  const [pin, setPin] = useState('');
  const confirm = useMutation({
    mutationFn: () => requests.confirmReceipt(r.id, pin.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recipient', 'requests'] });
      toast.success('Delivery confirmed — mèsi! Your receipt is on the way.');
    },
    onError: (e: Error) => toast.error(e.message || 'That code is incorrect.'),
  });

  return (
    <div className="rounded-lg border border-emerald-200 bg-white p-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[12px] text-cyan-700">REQ-{String(r.id).padStart(4, '0')}</span>
        <span className="text-[13px] font-semibold text-slate-900">· {r.category?.label ?? '—'}</span>
        <StatusPill status={r.status} />
      </div>
      <p className="mt-1 text-[12px] text-slate-600 inline-flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-slate-400" /> Enter the delivery code you received (by WhatsApp or from your family):</p>
      <div className="mt-2 flex items-center gap-2">
        <input
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          placeholder="6-digit code"
          className="w-36 px-3 py-2 text-[16px] font-mono tracking-[0.3em] text-center border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
          onKeyDown={(e) => { if (e.key === 'Enter' && pin.length >= 4) confirm.mutate(); }}
        />
        <Button tone="primary" onClick={() => confirm.mutate()} loading={confirm.isPending} disabled={pin.length < 4}>
          <CheckCircle2 className="w-4 h-4 mr-1" /> Confirm receipt
        </Button>
      </div>
    </div>
  );
}
